import StateFilteredAddress from '../../forms/address/StateFilteredAddress';
import { StateFilterData } from '../../forms/address/types';
import FormHandler from '../../forms/FormHandler';
import ajax from '../../utils/ajax';
import dom from '../../utils/dom';
import announce from '../../utils/screen-reader/announce';
import ScreenReaderLoadingMessages from '../../utils/screen-reader/ScreenReaderLoadingMessages';
import AnalyticsService from '../../analytics/AnalyticsService';
import { IViewItemAnalytics } from '../../analytics/types';
import fireAddToCartAnalytics from '../../analytics/fireAddToCartAnalytics';

interface ContributionProduct {
  id: number;
  onAccountId: number;
  slug: string;
}

interface ContributionFormComponent {
  metaTagTitle: string;
  formTemplateKeys: Array<string>;
  isSuggestedAmountEditable: boolean;
  isMembershipUpgrade: boolean;
  isMembershipRenewal: boolean;
  maximumAmount: number;
  minimumAmount: number;
  suggestedAmount: number;
  contributionTypeText?: string;
  suggestedAmountDisplay?: string;
}

export interface Contribution {
  contributionProduct: ContributionProduct;
  contributionFormComponent: ContributionFormComponent;
}

function getContributionsMap(contributionTypes:Array<Contribution> = []) {
  return new Map(contributionTypes.map((contributionType) => {
    const key = contributionType.contributionProduct.id.toString();

    return [key, contributionType];
  }));
}

class ContributionGroupForm {
  contributions: Map<string, Contribution>;

  addedContributionAmount: string;

  addedContributionId: string;

  $contributionSelect: HTMLSelectElement;

  $textContainer: HTMLDivElement;

  $form: HTMLFormElement;

  formHandler: FormHandler;

  stateFilterData: StateFilterData;

  $submitButton: HTMLButtonElement;

  private viewItemAnalyticsMap: Record<string, IViewItemAnalytics>;

  constructor(contributionTypes: Array<Contribution>, stateFilterData: StateFilterData, addedContributionId: string | null, addedContributionAmount: string | null, viewItemAnalyticsMap: Record<string, IViewItemAnalytics>) {
    this.contributions = getContributionsMap(contributionTypes);
    this.stateFilterData = stateFilterData;
    this.viewItemAnalyticsMap = viewItemAnalyticsMap || {};

    this.$form = dom.find('#tn-contribution-form') as HTMLFormElement;
    this.$contributionSelect = dom.find('#AddContribution_ContributionTypeId') as HTMLSelectElement;
    this.$textContainer = dom.find('.tn-contribution-type-text') as HTMLDivElement;
    this.$submitButton = dom.find('#tn-contribution-submit') as HTMLButtonElement;

    this.formHandler = new FormHandler(this.$form, {
      onValidationSuccess: () => {
        const selectedId = this.$contributionSelect.value;
        const selectedAnalytics = this.viewItemAnalyticsMap[selectedId];
        const amountInput = this.getAmountInput();
        const amount = parseFloat(amountInput?.input?.value || '0') || 0;

        fireAddToCartAnalytics(selectedAnalytics, [{ price: amount, quantity: 1, itemVariant: '' }]);

        this.$form.submit();
      },
    });
    this.initAddressFilters();

    this.addedContributionId = addedContributionId || '';
    this.addedContributionAmount = addedContributionAmount || '';

    this.$contributionSelect.addEventListener('change', () => {
      const id = this.$contributionSelect.value;

      this.loadContribution(id);
    });
  }

  async loadContribution(id: string) {
    const contribution = this.contributions.get(id);

    const amountInput = this.getAmountInput();

    amountInput.clearErrors();

    let text = '';
    let userAmount = '';

    if (contribution) {
      const analytics = this.viewItemAnalyticsMap[id];
      if (analytics) {
        AnalyticsService.trackViewItem(analytics);
      }

      const component = contribution.contributionFormComponent;

      text = component.contributionTypeText || '';

      if (component.suggestedAmount > 0 && component.suggestedAmountDisplay) {
        userAmount = component.suggestedAmountDisplay;
      }

      if (component.isSuggestedAmountEditable) {
        amountInput.setIsRequired(true);
        amountInput.input.isReadonly = false;

        if (this.addedContributionId === id) {
          userAmount = this.addedContributionAmount;
        }
      } else {
        amountInput.clearErrors();
        amountInput.setIsRequired(false);
        amountInput.input.isReadonly = true;
      }

      dom.unhide(amountInput.$group);
      dom.unhide(this.$submitButton);
    } else {
      dom.hide(amountInput.$group);
      dom.hide(this.$submitButton);
    }

    this.$textContainer.innerHTML = text;

    await this.loadForms(contribution, userAmount);
  }

  private getAmountInput() {
    return this.formHandler.getControlGroup('AddContribution.Amount')!;
  }

  private async loadForms(contribution?: Contribution, userAmount:string = '') {
    const $formContainer = dom.find('.tn-contribution-type-form-container') as HTMLDivElement;
    const $busySpinner = dom.find('.tn-form-loader') as HTMLDivElement;

    dom.empty($formContainer);
    dom.hide($formContainer);

    this.formHandler.clearErrorMessage();
    this.formHandler.stopListening();

    if (contribution) {
      const keys = contribution.contributionFormComponent.formTemplateKeys;

      if (keys && keys.length) {
        dom.unhide($busySpinner);

        announce(ScreenReaderLoadingMessages.Start);

        const { result } = await ajax.get('/api/forms/html', { keys: keys.join(',') });

        dom.hide($busySpinner);

        if (result) {
          $formContainer.innerHTML = result;

          this.formHandler.reloadControlGroups();
          this.initAddressFilters();

          dom.unhide($formContainer);
        }

        announce(ScreenReaderLoadingMessages.Complete);
      }
    }

    const amountInput = this.getAmountInput();

    amountInput.input.setValueSilent(userAmount);

    this.formHandler.startListening();
  }

  initAddressFilters() {
    StateFilteredAddress.createForCustomFormFields(this.$form, this.formHandler, this.stateFilterData);
  }
}

export default ContributionGroupForm;
